import { useQuery } from "@tanstack/react-query";

import { apiBase, fetchUsingApi, fetchWithAuth } from "@/api/rest/core/apiClient";

import i18n from "@/i18n.tsx";

import { sendToSentry } from "@/utils/sentry";

import { appendUploadsToFormData, UploadedFile } from "./uploadService";

import { ApiResponse } from "../core/types";
import { Document, DocumentPreviewEvent, DocumentPreviewViewer, DocumentRaw } from "../models/document";

export const deleteDocument = async (buildingId: number, householdId: number, documentId: number): Promise<void> => {
  const result = await fetchWithAuth(
    `${apiBase}/buildings/${buildingId}/households/${householdId}/documents/${documentId}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  if (result.status === 200) {
    return;
  }
  throw i18n.t("common.error.500");
};

export const uploadDocuments = async (householdId: number, uploads: UploadedFile[]): Promise<ApiResponse<boolean>> => {
  const formData = new FormData();
  formData.append("householdId", householdId.toString());
  appendUploadsToFormData(formData, uploads);
  return await fetchUsingApi(`${apiBase}/documents/v1/upload`, "POST", (e) => e as boolean, formData);
};

export const noJSDownload = (url: string, filename: string | null) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || ""; // Empty string will use server's filename
  link.target = "_blank";
  link.rel = "noopener noreferrer";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

function addDownloadParam(url: string): string {
  const urlObj = new URL(url);
  urlObj.searchParams.set("download", "true");
  return urlObj.toString();
}

export const downloadDocument2 = async (url: string, originalName: string | null, forceDownload: boolean = false) => {
  url = addDownloadParam(url);
  try {
    if (forceDownload) {
      noJSDownload(url, originalName);
      return;
    }

    noJSDownload(url, originalName);
  } catch (error) {
    sendToSentry(error as Error, {
      additionalContext: {
        component: "downloadDocument",
        url,
        originalName,
      },
    });

    throw i18n.t("common.error.500");
  }
};

export const loadDocument = async (doc: Document): Promise<string> => {
  const result = await fetchWithAuth(getDocumentUrl2(doc), {
    method: "GET",
  });
  if (result.status === 200) {
    return await result.text();
  }
  throw i18n.t("common.error.500");
};

// Fire-and-forget: telemetry must never break or delay a preview. keepalive lets CLOSED survive closing the tab.
export const logDocumentPreviewEvent = (
  documentId: number,
  viewer: DocumentPreviewViewer,
  event: DocumentPreviewEvent,
): void => {
  fetchWithAuth(`${apiBase}/documents/v1/${documentId}/preview-events`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ viewer, event }),
    keepalive: true,
    retry: true,
  }).catch(() => undefined);
};

export const getDocumentUrl2 = (doc: Document | null, useHash = false, value?: string) => {
  if (!doc && !value) {
    throw new Error("Neither document nor custom value provided");
  }
  if (useHash) {
    return `${apiBase}/documents/v1/download/hash/${doc?.hash ?? value}`;
  }
  return `${apiBase}/documents/v1/download/id/${doc?.id ?? value}`;
};

// For <video>: the backend redirects to storage, which answers the range requests players send.
export const getDocumentStreamUrl = (hash: string) => `${apiBase}/documents/v1/download/hash/${hash}/stream`;

export const getBuildingDocuments = async (buildingId: number): Promise<DocumentRaw[]> => {
  const result = await fetchWithAuth(`${apiBase}/buildings/${buildingId}/documents`, {
    method: "GET",
  });
  if (result.status === 200) {
    return (await result.json()) as DocumentRaw[];
  }
  throw i18n.t("common.error.500");
};

export const useBuildingDocuments = (buildingId: number) => {
  return useQuery({
    queryKey: ["building-documents", buildingId],
    queryFn: () => getBuildingDocuments(buildingId),
    enabled: buildingId > 0,
  });
};
